import React, { useCallback, useEffect, useState } from 'react';
import {
    Show,
    SimpleShowLayout,
    TextField,
    EmailField,
    FunctionField,
    Labeled,
    useRecordContext,
    useRefresh,
    usePermissions,
} from 'react-admin';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField as MuiTextField,
    Tooltip,
    Typography,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
    listEmployeeDocuments,
    downloadEmployeeDocument,
    terminateEmployee,
} from '../dataProvider';
import type { DocType } from './EmployeeDocumentsSection';
import type { DocumentsMap } from './EmployeeDocumentsSection';
import DocumentViewerDialog from './DocumentViewerDialog';

/**
 * Calculates years of service from a join date.
 * Returns the number of full years between the join date and today.
 */
function calculateYearsOfService (joinDate: string | null | undefined): number {
    if (!joinDate) return 0;
    const join = new Date(joinDate);
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - join.getTime());
    const diffYears = diffTime / (365.25 * 24 * 60 * 60 * 1000);
    return Math.floor(diffYears);
}

/**
 * Formats a byte count as a human-readable string (KB or MB).
 *
 * @param bytes - Raw byte count.
 * @returns Formatted string such as "12.3 KB" or "4.5 MB".
 */
function formatBytes (bytes: number): string {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * A section wrapper that renders a bold heading above a group of fields.
 *
 * Used to visually cluster related fields on the employee show page
 * so that related data is presented near each other, improving readability.
 *
 * @param props.title - The section heading text.
 * @param props.children - The field components to render inside the section.
 */
export const FieldGroup: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, borderBottom: '1px solid', borderColor: 'divider', pb: 0.5 }}>
            {title}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {children}
        </Box>
    </Box>
);

// ---------------------------------------------------------------------------
// Document slot configuration
// ---------------------------------------------------------------------------

/** A document slot's title in both English and Vietnamese. */
interface DocTypeLabel {
    /** English title. */
    en: string;
    /** Vietnamese title. */
    vi: string;
}

/** Human-readable English + Vietnamese titles for each document slot. */
const DOC_TYPE_LABELS: Record<DocType, DocTypeLabel> = {
    id_card: { en: 'ID Card / CCCD', vi: 'CCCD' },
    health_insurance_book: { en: 'Health Insurance Book Cover', vi: 'Bìa sổ bảo hiểm y tế' },
    health_insurance_card: { en: 'Health Insurance Card', vi: 'Thẻ bảo hiểm y tế' },
    vneid_record: { en: 'VNeID Verified Electronic Record', vi: 'Hồ sơ điện tử xác thực VNeID' },
    cv_certified: { en: 'Locally-Certified Curriculum Vitae', vi: 'Sơ yếu lý lịch có chứng thực' },
    resignation_letter: { en: 'Resignation Letter', vi: 'Đơn xin nghỉ việc' },
    resignation_settlement: { en: 'Resignation Settlement Record', vi: 'Biên bản thanh lý nghỉ việc' },
    asset_return: { en: 'Asset Return Record', vi: 'Biên bản hoàn trả tài sản' },
    post_employment_confidentiality: {
        en: 'Post-Employment Confidentiality Commitment',
        vi: 'Cam kết bảo mật thông tin sau khi nghỉ việc',
    },
    payment_policy_agreement: { en: 'Payment Policy Agreement', vi: 'Thỏa thuận chính sách thanh toán' },
    probation_contract: { en: 'Probation/Collaborator Contract', vi: 'Hợp đồng thử việc-CTV' },
    asset_handover: { en: 'Asset Handover', vi: 'Bàn giao tài sản' },
    dependent_proof_1: { en: 'Proof of Dependent 1', vi: 'Chứng từ người phụ thuộc 1' },
    dependent_proof_2: { en: 'Proof of Dependent 2', vi: 'Chứng từ người phụ thuộc 2' },
    dependent_proof_3: { en: 'Proof of Dependent 3', vi: 'Chứng từ người phụ thuộc 3' },
    dependent_proof_4: { en: 'Proof of Dependent 4', vi: 'Chứng từ người phụ thuộc 4' },
    dependent_proof_5: { en: 'Proof of Dependent 5', vi: 'Chứng từ người phụ thuộc 5' },
    dependent_proof_6: { en: 'Proof of Dependent 6', vi: 'Chứng từ người phụ thuộc 6' },
    dependent_proof_7: { en: 'Proof of Dependent 7', vi: 'Chứng từ người phụ thuộc 7' },
    dependent_proof_8: { en: 'Proof of Dependent 8', vi: 'Chứng từ người phụ thuộc 8' },
    dependent_proof_9: { en: 'Proof of Dependent 9', vi: 'Chứng từ người phụ thuộc 9' },
    dependent_proof_10: { en: 'Proof of Dependent 10', vi: 'Chứng từ người phụ thuộc 10' },
    termination_decision: {
        en: 'Decision on Termination of Labor Contract',
        vi: 'Quyết định chấm dứt hợp đồng lao động',
    },
};

/**
 * Formats a slot's EN + VN titles into a single display/accessibility string.
 *
 * Accepts an already-resolved {@link DocTypeLabel} (rather than a `DocType`) so
 * callers that have already looked up `DOC_TYPE_LABELS[docType]` for rendering
 * can reuse that same lookup instead of hitting the map twice per render.
 *
 * @param label - The slot's EN + VN titles.
 * @returns A string of the form `"English Title / Vietnamese Title"`.
 */
function formatLabel (label: DocTypeLabel): string {
    return `${label.en} / ${label.vi}`;
}

/** Ordered list of slot identifiers for consistent rendering. */
const DOC_TYPES: DocType[] = [
    'id_card',
    'health_insurance_book',
    'health_insurance_card',
    'vneid_record',
    'cv_certified',
    'resignation_letter',
    'resignation_settlement',
    'asset_return',
    'post_employment_confidentiality',
    'payment_policy_agreement',
    'probation_contract',
    'asset_handover',
    'dependent_proof_1',
    'dependent_proof_2',
    'dependent_proof_3',
    'dependent_proof_4',
    'dependent_proof_5',
    'dependent_proof_6',
    'dependent_proof_7',
    'dependent_proof_8',
    'dependent_proof_9',
    'dependent_proof_10',
    'termination_decision',
];

// ---------------------------------------------------------------------------
// ReadOnlyDocumentsSection
// ---------------------------------------------------------------------------

/**
 * Read-only view of the five fixed employee document slots rendered inside
 * the `EmployeeShow` page.
 *
 * Loads the document metadata for the record currently in the react-admin
 * record context and renders each slot with:
 *   - Document label
 *   - Current filename and file size (when a file is stored), or "No file uploaded"
 *   - A **View** icon button that opens {@link DocumentViewerDialog} for inline
 *     preview of images and PDFs
 *   - A download icon button that streams the file from R2 via the API
 *
 * Upload and delete actions are intentionally omitted — this component is
 * embedded in the read-only show page only.
 *
 * @param props.accountingId - The numeric `id` of the `employees` row.
 */
const ReadOnlyDocumentsSection: React.FC<{ accountingId: number | string }> = ({
    accountingId,
}) => {
    const [documents, setDocuments] = useState<DocumentsMap | null>(null);
    const [isFetching, setIsFetching] = useState(true);
    const [downloadingSlots, setDownloadingSlots] = useState<Set<DocType>>(new Set());
    const [viewerDocType, setViewerDocType] = useState<DocType | null>(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const docs = await listEmployeeDocuments(accountingId);
                if (!cancelled) setDocuments(docs as DocumentsMap);
            } catch {
                // Leave documents as null — slots will render as empty.
            } finally {
                if (!cancelled) setIsFetching(false);
            }
        };
        void load();
        return () => { cancelled = true; };
    }, [accountingId]);

    const handleDownload = useCallback(
        async (docType: DocType) => {
            setDownloadingSlots(prev => new Set(prev).add(docType));
            try {
                await downloadEmployeeDocument(accountingId, docType);
            } finally {
                setDownloadingSlots(prev => {
                    const next = new Set(prev);
                    next.delete(docType);
                    return next;
                });
            }
        },
        [accountingId]
    );

    if (isFetching) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    return (
        <Box data-testid="show-employee-documents-section" sx={{ width: '100%' }}>
            {DOC_TYPES.map(docType => {
                const meta = documents?.[docType] ?? null;
                const docTypeLabel = DOC_TYPE_LABELS[docType];
                const { en: labelEn, vi: labelVi } = docTypeLabel;
                const label = formatLabel(docTypeLabel);
                const isDownloading = downloadingSlots.has(docType);

                return (
                    <Box
                        key={docType}
                        data-testid={`show-doc-slot-${docType}`}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            py: 1,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            flexWrap: 'wrap',
                        }}
                    >
                        {/* Label — shown in both English and Vietnamese */}
                        <Box sx={{ minWidth: 260, display: 'flex', alignItems: 'center', gap: 0.5 }} data-testid={`show-doc-label-${docType}`}>
                            <InsertDriveFileIcon fontSize="small" color={meta ? 'primary' : 'disabled'} />
                            <Box>
                                <Typography variant="body2" component="div">{labelEn}</Typography>
                                <Typography variant="caption" color="text.secondary" component="div">{labelVi}</Typography>
                            </Box>
                        </Box>

                        {/* File info */}
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                            {meta ? (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    data-testid={`show-doc-filename-${docType}`}
                                >
                                    {meta.original_filename} ({formatBytes(meta.size_bytes)})
                                </Typography>
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="text.disabled"
                                    data-testid={`show-doc-empty-${docType}`}
                                >
                                    No file uploaded
                                </Typography>
                            )}
                        </Box>

                        {/* Actions (only when a file exists) */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {meta && (
                                isDownloading ? (
                                    <CircularProgress size={20} data-testid={`show-doc-loading-${docType}`} />
                                ) : (
                                    <>
                                        <Tooltip title={`View ${label}`}>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setViewerDocType(docType)}
                                                    data-testid={`show-doc-view-${docType}`}
                                                    aria-label={`View ${label}`}
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title={`Download ${label}`}>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDownload(docType)}
                                                    data-testid={`show-doc-download-${docType}`}
                                                    aria-label={`Download ${label}`}
                                                >
                                                    <DownloadIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </>
                                )
                            )}
                        </Box>
                    </Box>
                );
            })}

            {/* Document viewer dialog */}
            {viewerDocType && documents?.[viewerDocType] && (
                <DocumentViewerDialog
                    open={viewerDocType !== null}
                    onClose={() => setViewerDocType(null)}
                    accountingId={accountingId}
                    docType={viewerDocType}
                    label={formatLabel(DOC_TYPE_LABELS[viewerDocType])}
                />
            )}
        </Box>
    );
};

// ---------------------------------------------------------------------------
// DocumentsSectionWrapper
// ---------------------------------------------------------------------------

/**
 * Thin wrapper that reads the current react-admin record and passes its `id`
 * down to {@link ReadOnlyDocumentsSection}.
 *
 * Placed inside the `<Show>` context so `useRecordContext` resolves correctly.
 */
const DocumentsSectionWrapper: React.FC = () => {
    const record = useRecordContext();
    if (!record?.id) return null;
    return <ReadOnlyDocumentsSection accountingId={record.id} />;
};

// ---------------------------------------------------------------------------
// TerminateDialog
// ---------------------------------------------------------------------------

/**
 * Props for the {@link TerminateDialog} component.
 */
interface TerminateDialogProps {
    /** Whether the dialog is open. */
    open: boolean;
    /** The minimum allowed termination date (employee's join_date or today). */
    minDate: string;
    /** Callback invoked when the user cancels. */
    onClose: () => void;
    /**
     * Callback invoked when the user submits the dialog.
     * @param terminationDate - The ISO 8601 date chosen by the user.
     */
    onSubmit: (terminationDate: string) => Promise<void>;
}

/**
 * Dialog for initiating the termination workflow for an employee.
 *
 * Renders a date picker defaulting to today's date.  The minimum selectable
 * date is either the employee's `join_date` or today, whichever is more recent.
 * On submit, calls `terminateEmployee` via the data provider and refreshes the
 * parent record.
 *
 * @param props - See {@link TerminateDialogProps}.
 */
const TerminateDialog: React.FC<TerminateDialogProps> = ({ open, minDate, onClose, onSubmit }) => {
    const today = new Date().toISOString().slice(0, 10);
    const [date, setDate] = useState<string>(today);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async () => {
        setError(null);
        setSubmitting(true);
        try {
            await onSubmit(date);
            onClose();
        } catch (err) {
            setError((err as Error).message ?? 'Termination failed');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Terminate Employee</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Select the termination date. This will set the employee&apos;s employment end date and
                    automatically create an offboarding checklist.
                </Typography>
                <MuiTextField
                    label="Termination Date"
                    type="date"
                    value={date}
                    onChange={e => setDate(e.target.value)}
                    inputProps={{ min: minDate }}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    data-testid="termination-date-input"
                />
                {error && (
                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                        {error}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    disabled={submitting || !date}
                    variant="contained"
                    color="error"
                    data-testid="terminate-submit-button"
                >
                    {submitting ? 'Terminating…' : 'Terminate'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ---------------------------------------------------------------------------
// TerminateButton
// ---------------------------------------------------------------------------

/**
 * Button that opens the {@link TerminateDialog} for the currently viewed
 * employee record.
 *
 * Only renders when:
 * - The current user holds the `employees.manage` permission.
 * - The record's `status` is `'active'` (not yet terminated).
 *
 * On successful termination, refreshes the react-admin record to reflect the
 * new `termination_date` and `status`, then displays the generated checklist.
 */
const TerminateButton: React.FC = () => {
    const record = useRecordContext<{
        id: number;
        status?: string;
        join_date?: string;
        full_name?: string;
    }>();
    const refresh = useRefresh();
    const { permissions } = usePermissions<string[]>();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [checklist, setChecklist] = useState<any[] | null>(null);

    if (!record) return null;
    if (!Array.isArray(permissions) || !permissions.includes('employees.manage')) return null;
    if (record.status === 'terminated') return null;

    const today = new Date().toISOString().slice(0, 10);
    const minDate = record.join_date && record.join_date > today ? record.join_date : today;

    const handleSubmit = async (terminationDate: string) => {
        const result = await terminateEmployee(record.id, terminationDate);
        setChecklist(result.checklist ?? null);
        refresh();
    };

    return (
        <>
            <Button
                variant="outlined"
                color="error"
                startIcon={<BlockIcon />}
                onClick={() => setDialogOpen(true)}
                data-testid="terminate-button"
                sx={{ mb: 2 }}
            >
                Terminate Employee
            </Button>
            <TerminateDialog
                open={dialogOpen}
                minDate={minDate}
                onClose={() => setDialogOpen(false)}
                onSubmit={handleSubmit}
            />
            {checklist && checklist.length > 0 && (
                <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'warning.main', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'warning.dark' }}>
                        Offboarding checklist created ({checklist.length} tasks)
                    </Typography>
                    {checklist.map((item: any) => (
                        <Typography key={item.id} variant="body2">
                            • {item.task_type.replace(/_/g, ' ')} — assigned to {item.assignee_email}
                        </Typography>
                    ))}
                </Box>
            )}
        </>
    );
};

// ---------------------------------------------------------------------------
// EmployeeShow
// ---------------------------------------------------------------------------

/**
 * Displays a read-only view of a single `employees` record (employment data).
 *
 * Fields are grouped by category so that related information is displayed
 * near each other:
 *
 * **Personal Information**
 * - Full Name (Vietnamese), English Name, Email, Gender, Date of Birth, Phone
 *
 * **Employment Details**
 * - Department, Title (Vietnamese), Title (English), Contract Type,
 *   Join Date, Join Date (ISO), Official Contract Date, Years of Service, Level
 *
 * **Identification & Address**
 * - ID Number, ID Issue Date, ID Issue Place, Address, Birth Month
 *
 * **Banking Information**
 * - Bank Name, Account Number
 *
 * **Notes**
 * - Note (free-text remarks)
 *
 * **Documents**
 * - Listing of all five fixed document slots with download action for
 *   any slot that has an uploaded file.
 *
 * @returns A react-admin `<Show>` component rendering all employee fields.
 */
export const EmployeeShow: React.FC = () => (
    <Show>
        <SimpleShowLayout>
            {/* ── Termination Action ───────────────────────────────── */}
            <TerminateButton />

            {/* ── Personal Information ─────────────────────────────── */}
            <FieldGroup title="Personal Information">
                <Labeled label="Employee ID">
                    <TextField source="employee_id" />
                </Labeled>
                <Labeled label="Full Name (Vietnamese)">
                    <TextField source="full_name" />
                </Labeled>
                <Labeled label="English Name">
                    <TextField source="english_name" />
                </Labeled>
                <Labeled label="Email">
                    <EmailField source="email" />
                </Labeled>
                <Labeled label="Gender">
                    <TextField source="gender" />
                </Labeled>
                <Labeled label="Date of Birth">
                    <TextField source="dob" />
                </Labeled>
                <Labeled label="Phone">
                    <TextField source="phone" />
                </Labeled>
            </FieldGroup>

            {/* ── Employment Details ───────────────────────────────── */}
            <FieldGroup title="Employment Details">
                <Labeled label="Department">
                    <TextField source="department" />
                </Labeled>
                <Labeled label="Title (Vietnamese)">
                    <TextField source="title" />
                </Labeled>
                <Labeled label="Title (English)">
                    <TextField source="title_en" />
                </Labeled>
                <Labeled label="Contract Type">
                    <TextField source="contract_type" />
                </Labeled>
                <Labeled label="Join Date">
                    <TextField source="join_date" />
                </Labeled>
                <Labeled label="Official Contract Date">
                    <TextField source="official_contract_date" />
                </Labeled>
                <Labeled label="Termination Date">
                    <TextField source="termination_date" />
                </Labeled>
                <Labeled label="Status">
                    <TextField source="status" />
                </Labeled>
                <Labeled label="Years of Service">
                    <FunctionField
                        source="join_date"
                        render={(record: any) => `${calculateYearsOfService(record?.join_date)} years`}
                    />
                </Labeled>
                <Labeled label="Level">
                    <TextField source="level" />
                </Labeled>
            </FieldGroup>

            {/* ── Identification & Address ─────────────────────────── */}
            <FieldGroup title="Identification & Address">
                <Labeled label="ID Number">
                    <TextField source="id_number" />
                </Labeled>
                <Labeled label="ID Issue Date">
                    <TextField source="id_issue_date" />
                </Labeled>
                <Labeled label="ID Issue Place">
                    <TextField source="id_issue_place" />
                </Labeled>
                <Labeled label="Address">
                    <TextField source="address" />
                </Labeled>
            </FieldGroup>

            {/* ── Banking Information ──────────────────────────────── */}
            <FieldGroup title="Banking Information">
                <Labeled label="Bank Name">
                    <TextField source="bank_name" />
                </Labeled>
                <Labeled label="Account Number">
                    <TextField source="account_number" />
                </Labeled>
            </FieldGroup>

            {/* ── Notes ────────────────────────────────────────────── */}
            <FieldGroup title="Notes">
                <Labeled label="Note">
                    <TextField source="note" />
                </Labeled>
            </FieldGroup>

            {/* ── Documents ────────────────────────────────────────── */}
            <FieldGroup title="Documents">
                <DocumentsSectionWrapper />
            </FieldGroup>
        </SimpleShowLayout>
    </Show>
);
